import { useAuthStore } from './auth-store';
import type { components } from '@/api/schema';

/**
 * The one place the api.Response envelope is unwrapped.
 *
 * Every response in this API — success and failure alike — is
 * `{status, message, error?, data?, meta?}`. Unwrapping it per call site would
 * mean twenty places that each decide what "failed" means; unwrapping it here
 * means one. Callers get `data` or an ApiError, never an envelope.
 */

export const API_BASE = '/api/v1';

type Envelope<T> = {
  status: boolean;
  message: string;
  error?: string;
  data?: T;
  meta?: components['schemas']['Meta'];
};

export class ApiError extends Error {
  readonly httpStatus: number;
  /** Stable machine code from `error`. Switch on this, never on `message`. */
  readonly code: string;
  /** Structured detail from `data` when the endpoint documents one. */
  readonly detail: unknown;

  constructor(httpStatus: number, code: string, message: string, detail?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.httpStatus = httpStatus;
    this.code = code;
    this.detail = detail;
  }

  /** A 409 from CanPrompt — carries the same reason code that greyed the composer. */
  get reasonCode(): components['schemas']['ComposerReasonCode'] | null {
    if (this.httpStatus !== 409) return null;
    const d = this.detail as { reason_code?: string } | undefined;
    return (d?.reason_code as components['schemas']['ComposerReasonCode'] | undefined) ?? null;
  }

  get composer(): components['schemas']['ComposerState'] | null {
    const d = this.detail as { composer?: components['schemas']['ComposerState'] } | undefined;
    return d?.composer ?? null;
  }

  get retryAfterSeconds(): number | null {
    const d = this.detail as { retry_after_seconds?: number | null } | undefined;
    return d?.retry_after_seconds ?? null;
  }

  get fieldErrors(): components['schemas']['FieldError'][] {
    const d = this.detail as { fields?: components['schemas']['FieldError'][] } | undefined;
    return d?.fields ?? [];
  }
}

/** Thrown when the session is gone for good and the user must sign in again. */
export class SessionExpiredError extends ApiError {
  constructor() {
    super(401, 'session_expired', 'Your session ended. Sign in again to continue.');
    this.name = 'SessionExpiredError';
  }
}

// ---------------------------------------------------------------------------
// Refresh: single-flight, and never retried on failure.
//
// Reuse of an already-rotated refresh token revokes the whole family — that is
// the theft signal — so a failed refresh means "sign in again", never "try
// again". Retrying here would turn a stolen-token alarm into a login loop.
// ---------------------------------------------------------------------------

let refreshInFlight: Promise<string | null> | null = null;

export async function refreshSession(): Promise<string | null> {
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    try {
      const res = await fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: '{}',
      });
      if (!res.ok) return null;
      const body = (await res.json()) as Envelope<components['schemas']['Session']>;
      if (!body.status || !body.data) return null;

      useAuthStore.getState().setSession(body.data.access_token, body.data.user);
      scheduleProactiveRefresh(body.data.expires_in);
      return body.data.access_token;
    } catch {
      return null;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

let refreshTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Refresh a minute before expiry. A long-lived SSE connection outlives its
 * access token, and a stream that dies at the token boundary looks to a client
 * exactly like the platform losing their work.
 */
function scheduleProactiveRefresh(expiresInSeconds: number): void {
  if (refreshTimer) clearTimeout(refreshTimer);
  const lead = Math.max(30, expiresInSeconds - 60);
  refreshTimer = setTimeout(() => {
    void refreshSession();
  }, lead * 1000);
}

export function stopProactiveRefresh(): void {
  if (refreshTimer) clearTimeout(refreshTimer);
  refreshTimer = null;
}

// ---------------------------------------------------------------------------
// The request wrapper
// ---------------------------------------------------------------------------

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** FormData for multipart uploads; the browser sets the boundary itself. */
  form?: FormData;
  query?: Record<string, string | number | boolean | null | undefined>;
  signal?: AbortSignal;
  /** Skip the Authorization header and the 401 retry (login-time calls). */
  anonymous?: boolean;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = `${API_BASE}${path}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === '') continue;
    params.set(k, String(v));
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

async function readEnvelope<T>(res: Response): Promise<Envelope<T>> {
  const text = await res.text();
  if (!text) {
    return { status: res.ok, message: res.ok ? 'ok' : `HTTP ${res.status}` };
  }
  try {
    return JSON.parse(text) as Envelope<T>;
  } catch {
    // A non-envelope body means something in front of the API answered — a
    // proxy error page, usually. Never surface its text: it is not ours and it
    // has not been through the scrub pipeline.
    return { status: false, message: 'The service is not responding correctly.', error: 'bad_gateway_body' };
  }
}

async function rawRequest(path: string, opts: RequestOptions, token: string | null): Promise<Response> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (token && !opts.anonymous) headers.Authorization = `Bearer ${token}`;

  let body: BodyInit | undefined;
  if (opts.form) {
    body = opts.form;
  } else if (opts.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(opts.body);
  }

  return fetch(buildUrl(path, opts.query), {
    method: opts.method ?? 'GET',
    credentials: 'include',
    headers,
    body,
    signal: opts.signal,
  });
}

export async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const token = opts.anonymous ? null : useAuthStore.getState().accessToken;

  let res = await rawRequest(path, opts, token);

  // Refresh once, then give up. Never twice: a second 401 after a fresh token
  // is a server decision, not a stale token.
  if (res.status === 401 && !opts.anonymous) {
    const fresh = await refreshSession();
    if (!fresh) {
      useAuthStore.getState().clear();
      throw new SessionExpiredError();
    }
    res = await rawRequest(path, opts, fresh);
    if (res.status === 401) {
      useAuthStore.getState().clear();
      throw new SessionExpiredError();
    }
  }

  const envelope = await readEnvelope<T>(res);

  if (!res.ok || envelope.status === false) {
    throw new ApiError(
      res.status,
      envelope.error ?? `http_${res.status}`,
      envelope.message || 'Something went wrong. Try again in a moment.',
      envelope.data,
    );
  }

  return envelope.data as T;
}

export const api = {
  get: <T>(path: string, opts?: Omit<RequestOptions, 'method' | 'body' | 'form'>) =>
    request<T>(path, { ...opts, method: 'GET' }),
  post: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...opts, method: 'POST', body }),
  postForm: <T>(path: string, form: FormData, opts?: Omit<RequestOptions, 'method' | 'body' | 'form'>) =>
    request<T>(path, { ...opts, method: 'POST', form }),
  patch: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...opts, method: 'PATCH', body }),
  del: <T>(path: string, opts?: Omit<RequestOptions, 'method' | 'body' | 'form'>) =>
    request<T>(path, { ...opts, method: 'DELETE' }),
};
